import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SpotifyPlayer from 'react-spotify-player';
import Dethklok from '../images/dethklok.gif';

const getPlayerHeight = width => {
  if (width >= 2560) return '900px';
  if (width >= 1920) return '800px';
  if (width <= 360) return '400px';
  if (width <= 390) return '450px';
  if (width <= 430) return '500px';
  return '400px';
};

const Media = () => {
  const [playerHeight, setPlayerHeight] = useState(() =>
    getPlayerHeight(typeof window !== 'undefined' ? window.innerWidth : 1920)
  );

  useEffect(() => {
    const handleResize = () =>
      setPlayerHeight(getPlayerHeight(window.innerWidth));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const size = { width: '100%', height: playerHeight };

  return (
    <Wrapper>
      <SpotifyWrapper>
        <SpotifyPlayer
          uri="https://open.spotify.com/playlist/6i1Basy0fXRW4HytYXHNF3?si=0a2101f1daa24e51"
          size={size}
          theme="black"
          view="list"
        />
      </SpotifyWrapper>

      <DethklokGif src={Dethklok} alt="Dethklok" />
    </Wrapper>
  );
};

export default Media;

const Wrapper = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  margin-top: 40px;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* Safari 100vh fix */
  min-height: -webkit-fill-available;

  /* Alienware Aurora 16X optimization (2560x1600) */
  @media (min-width: 2560px) {
    margin-top: 60px;
    padding: 40px 60px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    margin-top: 50px;
    padding: 30px 50px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    height: auto;
    min-height: auto;
    margin-top: 20px;
    padding: 10px;
    /* Safari mobile fix */
    min-height: -webkit-fill-available;
  }

  @media (max-width: 360px) {
    margin-top: 15px;
    padding: 8px;
  }

  /* Safari-specific fixes */
  @supports (-webkit-touch-callout: none) {
    min-height: -webkit-fill-available;
  }
`;

const DethklokGif = styled.img`
  margin: 40px;
  max-width: 600px;
  width: 100%;
  height: auto;
  min-height: 400px;
  object-fit: contain;

  /* Alienware Aurora 16X optimization (2560x1600) */
  @media (min-width: 2560px) {
    max-width: 900px;
    min-height: 600px;
    margin: 50px;
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    max-width: 750px;
    min-height: 500px;
    margin: 45px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    max-width: 95%;
    min-height: 250px;
    margin: 20px 10px;
  }

  @media (max-width: 390px) {
    max-width: 98%;
    min-height: 220px;
    margin: 15px 8px;
  }

  @media (max-width: 360px) {
    max-width: 100%;
    min-height: 200px;
    margin: 12px 5px;
  }
`;

const SpotifyWrapper = styled.div`
  -webkit-box-shadow: 10px 10px 39px 45px rgba(0, 0, 0, 0.87);
  -moz-box-shadow: 10px 10px 39px 45px rgba(0, 0, 0, 0.87);
  box-shadow: 10px 10px 39px 45px rgba(0, 0, 0, 0.87);
  margin-bottom: 30px;
  min-height: 400px;
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  position: relative;
  /* Safari rendering fix */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  /* Firefox rendering fix */
  -moz-transform: translateZ(0);

  /* Alienware Aurora 16X optimization (2560x1600) */
  @media (min-width: 2560px) {
    max-width: 900px;
    min-height: 900px;
    margin-bottom: 50px;
    -webkit-box-shadow: 15px 15px 50px 60px rgba(0, 0, 0, 0.87);
    -moz-box-shadow: 15px 15px 50px 60px rgba(0, 0, 0, 0.87);
    box-shadow: 15px 15px 50px 60px rgba(0, 0, 0, 0.87);
  }

  @media (min-width: 1920px) and (max-width: 2559px) {
    max-width: 750px;
    min-height: 800px;
    margin-bottom: 40px;
  }

  /* iPhone 15+ and Samsung Galaxy optimization */
  @media (max-width: 430px) {
    max-width: 95%;
    min-height: 500px;
    height: 500px;
    margin-bottom: 20px;
    padding: 0;
    box-shadow: 5px 5px 20px 20px rgba(0, 0, 0, 0.87);
  }

  @media (max-width: 390px) {
    max-width: 98%;
    min-height: 450px;
    height: 450px;
    margin-bottom: 15px;
    padding: 0;
    box-shadow: 4px 4px 18px 18px rgba(0, 0, 0, 0.87);
  }

  @media (max-width: 360px) {
    max-width: 100%;
    min-height: 400px;
    height: 400px;
    margin-bottom: 12px;
    padding: 0;
    box-shadow: 3px 3px 15px 15px rgba(0, 0, 0, 0.87);
  }

  /* Ensure iframe fills container */
  iframe {
    width: 100% !important;
    height: 100% !important;
    border: none;
    display: block;
    min-height: inherit;
  }

  /* Target react-spotify-player wrapper */
  > div {
    width: 100% !important;
    height: 100% !important;
    min-height: inherit;
    display: flex;
    align-items: stretch;
  }

  /* Ensure Spotify embed fills container on mobile */
  @media (max-width: 430px) {
    > div > iframe {
      width: 100% !important;
      height: 500px !important;
      min-height: 500px !important;
    }
  }

  @media (max-width: 390px) {
    > div > iframe {
      height: 450px !important;
      min-height: 450px !important;
    }
  }

  @media (max-width: 360px) {
    > div > iframe {
      height: 400px !important;
      min-height: 400px !important;
    }
  }
`;
